import { useState } from 'react';
import { api } from '../services/api';

const useOtmsService = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Organization Unit Services
  const organizationUnitService = {
    // Get all organization units
    getAllOrganizationUnits: async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get('/otms/organization-units');
        return response.data;
      } catch (err) {
        setError(err.response?.data?.message || 'Error fetching organization units');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Get organization unit by ID
    getOrganizationUnit: async (id) => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`/otms/organization-units/${id}`);
        return response.data;
      } catch (err) {
        setError(err.response?.data?.message || 'Error fetching organization unit');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Create organization unit
    createOrganizationUnit: async (data) => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.post('/otms/organization-units', data);
        return response.data;
      } catch (err) {
        setError(err.response?.data?.message || 'Error creating organization unit');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Update organization unit
    updateOrganizationUnit: async (id, data) => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.put(`/otms/organization-units/${id}`, data);
        return response.data;
      } catch (err) {
        setError(err.response?.data?.message || 'Error updating organization unit');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Delete organization unit
    deleteOrganizationUnit: async (id) => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.delete(`/otms/organization-units/${id}`);
        return response.data;
      } catch (err) {
        setError(err.response?.data?.message || 'Error deleting organization unit');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Get organization unit members
    getOrganizationUnitMembers: async (id) => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`/otms/organization-units/${id}/members`);
        
        // Get primary position for each member
        const membersWithDetails = await Promise.all(response.data.map(async (member) => {
          try {
            const positionResponse = await api.get(`/otms/personnel/primaryjob/${member.id}`);
            return {
              ...member,
              position: positionResponse.data
            };
          } catch (err) {
            console.error(`Error getting position for member ${member.id}:`, err);
            return member;
          }
        }));
        
        return membersWithDetails;
      } catch (err) {
        setError(err.response?.data?.message || 'Error fetching organization unit members');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Get organization unit charter
    getOrganizationUnitCharter: async (id) => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`/otms/organization-units/${id}/charter`);
        return response.data;
      } catch (err) {
        setError(err.response?.data?.message || 'Error fetching organization unit charter');
        throw err;
      } finally {
        setLoading(false);
      }
    }
  };

  const charterService = {
    // Get charter by organization unit ID
    getCharter: async (organizationUnitId) => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`/otms/charters/unit/${organizationUnitId}`);
        return response.data;
      } catch (err) {
        setError(err.response?.data?.message || 'Error fetching charter');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Create new charter
    createCharter: async (charter) => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.post('/otms/charters', charter);
        return response.data;
      } catch (err) {
        setError(err.response?.data?.message || 'Error creating charter');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Update charter
    updateCharter: async (charterId, charter) => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.put(`/otms/charters/${charterId}`, charter);
        return response.data;
      } catch (err) {
        setError(err.response?.data?.message || 'Error updating charter');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Delete charter
    deleteCharter: async (charterId) => {
      try {
        setLoading(true);
        setError(null);
        await api.delete(`/otms/charters/${charterId}`);
      } catch (err) {
        setError(err.response?.data?.message || 'Error deleting charter');
        throw err;
      } finally {
        setLoading(false);
      }
    }
  };

  const documentService = {
    // Search documents in organization unit
    searchDocuments: async (organizationUnitId, query) => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`/otms/documents/unit/${organizationUnitId}/search`, {
          params: { query }
        });
        return response.data;
      } catch (err) {
        setError(err.response?.data?.message || 'Error searching documents');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Upload document with repository option
    uploadDocument: async (file, organizationUnitId, documentInfo) => {
      try {
        setLoading(true);
        setError(null);
        const formData = new FormData();
        formData.append('file', file);
        formData.append('customerId', localStorage.getItem('customerId'));
        formData.append('organizationUnitId', organizationUnitId);
        formData.append('title', documentInfo.title);
        formData.append('type', documentInfo.type);
        formData.append('description', documentInfo.description);
        formData.append('addToRepository', documentInfo.addToRepository || false);
        formData.append('documentType', documentInfo.addToRepository ? 'WorkDocument' : 'TaskDocument');

        const response = await api.post('/otms/documents/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        return response.data;
      } catch (err) {
        setError(err.response?.data?.message || 'Error uploading document');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Get documents for organization unit
    getDocuments: async (organizationUnitId) => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`/otms/documents/unit/${organizationUnitId}`);
        return response.data;
      } catch (err) {
        setError(err.response?.data?.message || 'Error fetching documents');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Get document download URL
    getDocumentUrl: async (documentId) => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`/otms/documents/${documentId}/download`);
        return response.data;
      } catch (err) {
        setError(err.response?.data?.message || 'Error getting document URL');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Delete document
    deleteDocument: async (documentId) => {
      try {
        setLoading(true);
        setError(null);
        await api.delete(`/otms/documents/${documentId}`);
      } catch (err) {
        setError(err.response?.data?.message || 'Error deleting document');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Update document
    updateDocument: async (documentId, updates) => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.patch(`/otms/documents/${documentId}`, updates);
        return response.data;
      } catch (err) {
        setError(err.response?.data?.message || 'Error updating document');
        throw err;
      } finally {
        setLoading(false);
      }
    }
  };


  const sop = {
    getAll: async (params) => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`/otms/sops${params ? `?${params}` : ''}`);
        return { data: response.data };
      } catch (err) {
        setError(err.response?.data?.message || 'Error getting SOPs');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    getById: async (id) => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`/otms/sops/${id}`);
        return { data: response.data };
      } catch (err) {
        setError(err.response?.data?.message || 'Error getting SOP');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    create: async (sopData) => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.post('/otms/sops', sopData);
        return { data: response.data };
      } catch (err) {
        setError(err.response?.data?.message || 'Error creating SOP');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    update: async (id, sopData) => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.put(`/otms/sops/${id}`, sopData);
        return { data: response.data };
      } catch (err) {
        setError(err.response?.data?.message || 'Error updating SOP');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    delete: async (id) => {
      try {
        setLoading(true);
        setError(null);
        await api.delete(`/otms/sops/${id}`);
      } catch (err) {
        setError(err.response?.data?.message || 'Error deleting SOP');
        throw err;
      } finally {
        setLoading(false);
      }
    }
  };

  const personnel = {
    // Get all persons with pagination and filters
    getPersons: async (params) => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`/otms/personnel/persons${params ? `?${params}` : ''}`);
        console.log('persons' + response.data);
        return { data: response.data };
      } catch (err) {
        setError(err.response?.data?.message || 'Error getting persons');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Get person by ID
    getPersonById: async (id) => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`/otms/personnel/persons/${id}`);
        return { data: response.data };
      } catch (err) {
        setError(err.response?.data?.message || 'Error getting person');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Get person's primary position
    getPrimaryPosition: async (personId) => {
      try {
        setLoading(true);
        //setError(null);
        const response = await api.get(`/otms/personnel/primaryjob/${personId}`);
        return { data: response.data };
      } catch (err) {
        setError(err.response?.data?.message || 'Error getting primary position');
        //throw err;
      } finally {
        setLoading(false);
      }
    },

    // Create person
    createPerson: async (personData) => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.post('/otms/personnel/persons', personData);
        return { data: response.data };
      } catch (err) {
        setError(err.response?.data?.message || 'Error creating person');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Update person
    updatePerson: async (id, personData) => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.put(`/otms/personnel/persons/${id}`, personData);
        return { data: response.data };
      } catch (err) {
        setError(err.response?.data?.message || 'Error updating person');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Delete person
    deletePerson: async (id) => {
      try {
        setLoading(true);
        setError(null);
        await api.delete(`/otms/personnel/persons/${id}`);
      } catch (err) {
        setError(err.response?.data?.message || 'Error deleting person');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Get all positions
    getPositions: async (params) => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`/otms/personnel/positions${params ? `?${params}` : ''}`);
        return { data: response.data };
      } catch (err) {
        setError(err.response?.data?.message || 'Error getting positions');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    

    // Get position by ID
    getPositionById: async (id) => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`/otms/personnel/positions/${id}`);
        return { data: response.data };
      } catch (err) {
        setError(err.response?.data?.message || 'Error getting position');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Get position history with current and past holders
    getPositionHistoryforPosition: async (id) => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`/otms/personnel/positions/${id}/history`);
        return { data: response.data };
      } catch (err) {
        setError(err.response?.data?.message || 'Error getting position history');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Create position
    createPosition: async (positionData) => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.post('/otms/personnel/positions', positionData);
        return { data: response.data };
      } catch (err) {
        setError(err.response?.data?.message || 'Error creating position');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Update position
    updatePosition: async (id, positionData) => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.put(`/otms/personnel/positions/${id}`, positionData);
        return { data: response.data };
      } catch (err) {
        setError(err.response?.data?.message || 'Error updating position');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Delete position
    deletePosition: async (id) => {
      try {
        setLoading(true);
        setError(null);
        await api.delete(`/otms/personnel/positions/${id}`);
      } catch (err) {
        setError(err.response?.data?.message || 'Error deleting position');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Get position history for a person
    getPositionHistory: async (personId) => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`/otms/personnel/position-history/${personId}`);
        return { data: response.data };
      } catch (err) {
        setError(err.response?.data?.message || 'Error getting position history');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Assign position to person
    assignPosition: async (assignmentData) => {
      try {
        setLoading(true);
        setError(null);
        const { personId, ...data } = assignmentData;
        
        if (!personId) {
          throw new Error('PersonId is required for position assignment');
        }
        
        const response = await api.post(`/otms/personnel/persons/${personId}/positions`, data);
        return { data: response.data };
      } catch (err) {
        console.error('Position assignment error:', err);
        setError(err.response?.data?.message || err.message || 'Error assigning position');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Refresh persons from customer users
    refreshPersons: async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.post('/otms/personnel/refresh-persons');
        return { data: response.data };
      } catch (err) {
        setError(err.response?.data?.message || 'Error refreshing persons');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Assign role to person
    assignRole: async (personId, role) => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.post(`/otms/personnel/persons/${personId}/roles`, { role });
        return { data: response.data };
      } catch (err) {
        setError(err.response?.data?.message || 'Error assigning role');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Remove role from person
    removeRole: async (personId, roleId) => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.delete(`/otms/personnel/persons/${personId}/roles/${roleId}`);
        return { data: response.data };
      } catch (err) {
        setError(err.response?.data?.message || 'Error removing role');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Update current position
    updateCurrentPosition: async (personId, positionId, updateData) => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.put(`/otms/personnel/persons/${personId}/positions/current`, updateData);
        return { data: response.data };
      } catch (err) {
        setError(err.response?.data?.message || 'Error updating position');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Remove current position from person
    removePosition: async (personId) => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.delete(`/otms/personnel/persons/${personId}/positions/current`);
        return { data: response.data };
      } catch (err) {
        setError(err.response?.data?.message || 'Error removing position');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Get person's organization units with hierarchy
    getPersonOrganizationUnits: async (personId) => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`/otms/personnel/persons/${personId}/organization-units`);
        return { data: response.data };
      } catch (err) {
        setError(err.response?.data?.message || 'Error getting person organization units');
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Get current person ID
    getCurrentPersonId: async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get('/otms/personnel/current');
        return response.data?.id;
      } catch (err) {
        setError(err.response?.data?.message || 'Error getting current person');
        throw err;
      } finally {
        setLoading(false);
      }
    }
  };

  return {
    loading,
    error,
    organizationUnitService,
    charterService,
    documentService,
    sop,
    personnel
  };
};

export default useOtmsService; 